import axios from "axios";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import baseURL from "../../config/api";
import useActiveNavbar from "../../hook/useActiveNavbar";

interface KeywordItem {
    id: number;
    keywords: string;
    path: string;
}

const HelpDocHeader: React.FC = () => {
    const navigate = useNavigate();
    const { selected, setSelected }: any = useActiveNavbar();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [results, setResults] = useState<KeywordItem[]>([]);
    const [page, setPage] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const [openDropdown, setOpenDropdown] = useState<any | null>(null);

    const navItems = [
        {
            name: "Resources",
            children: [
                {
                    name: "Security",
                    link: "/security",
                },
                {
                    name: "Demos",
                    link: "/demos",
                },
                {
                    name: "Blog",
                    link: "/blog",
                },
                {
                    name: "Contact Us",
                    link: "/contact-us",
                },
            ],
        },
    ];

    const fetchSearchResults = async (
        pageNumber: number,
        keyword: string = ""
    ) => {
        try {
            setLoading(true);

            const endpoint = keyword.trim()
                ? `keyword/GetKeywordSearchsByKewordPaging?keyword=${encodeURIComponent(
                    keyword
                )}&page=${pageNumber}&size=10`
                : `keyword/GetKeywordSearchsPaging/${pageNumber},10`;

            const response = await axios.get(`${baseURL}${endpoint}`);

            setResults((prev) =>
                pageNumber === 0
                    ? response.data.keywords || []
                    : [...prev, ...(response.data.keywords || [])]
            );

            setHasMore((response.data.keywords || []).length === 10);
        } catch (error) {
            console.error("Error fetching search results:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!dropdownOpen) return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setPage(0);
            fetchSearchResults(0, searchTerm);
        }, 500);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, dropdownOpen]);

    useEffect(() => {
        if (page === 0) return;

        fetchSearchResults(page, searchTerm);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setDropdownOpen(false);
                setResults([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputClick = () => {
        setDropdownOpen(true);

        if (results.length === 0) {
            fetchSearchResults(0, searchTerm);
        }
    };

    const lastElementRef = useCallback(
        (node: HTMLLIElement | null) => {
            if (loading) return;

            if (observer.current) {
                observer.current.disconnect();
            }

            if (node && hasMore) {
                observer.current = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting) {
                        setPage((prevPage) => prevPage + 1);
                    }
                });

                observer.current.observe(node);
            }
        },
        [loading, hasMore]
    );

    const handleItemClick = (path: string) => {
        setSearchTerm("");
        setDropdownOpen(false);
        setResults([]);
        navigate(path);
    };

    return (
        <>
            <nav className="sticky px-4 md:px-[46px] py-[10px] top-0 z-[50] bg-white shadow-sm">
                <div className="w-full flex items-center justify-between">
                    <NavLink to="/">
                        <img
                            className="logo-img lg:w-[186px] w-[120px] h-auto"
                            src="/images/navbar_logo.png"
                            alt="logo"
                        />
                    </NavLink>

                    <div className="hidden lg:flex items-center gap-6">
                        <NavLink
                            to="/tutorial"
                            className={({ isActive }) =>
                                `text-sm hover:text-secondary ${isActive
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                }`
                            }
                        >
                            Overview
                        </NavLink>

                        <NavLink
                            to="/docs/introduction"
                            className={({ isActive }) =>
                                `text-sm hover:text-secondary  ${isActive
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                }`
                            }
                        >
                            Docs
                        </NavLink>

                        <NavLink
                            to="/features"
                            className={({ isActive }) =>
                                `text-sm hover:text-secondary  ${isActive
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                }`
                            }
                        >
                            Features
                        </NavLink>
                        {navItems.map((item, index) => (
                            <div key={index} className="relative group inline-block">
                                <button className="flex items-center text-sm gap-1 text-blackRgba">
                                    <span className={`text-sm hover:text-secondary ${item.children.some((c: any) => c.link === selected)
                                        ? "text-secondary font-medium"
                                        : "text-blackRgba"
                                        }`}>
                                        {item.name}
                                    </span>
                                    <span className="material-symbols-outlined">
                                        keyboard_arrow_down
                                    </span>
                                </button>


                                <div className="absolute top-full left-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition shadow-lg rounded-md z-10 bg-white">
                                    <ul className="min-w-[180px] py-2">
                                        {item.children.map((child: any, i: number) => (
                                            <li
                                                key={i}
                                                className={`px-4 py-2 text-sm hover:bg-gray-100 whitespace-nowrap ${selected === child.link
                                                    ? "text-secondary font-medium"
                                                    : "text-blackRgba"
                                                    }`}
                                            >
                                                <NavLink
                                                    to={child.link}
                                                    className="block w-full"
                                                    onClick={() => setSelected(child.link)}
                                                >
                                                    {child.name}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                        )}
                        <NavLink
                            to="/faq"
                            className={({ isActive }) =>
                                `text-sm hover:text-secondary ${isActive
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                }`
                            }

                        >
                            FAQ
                        </NavLink>

                        <NavLink
                            to="/pricing"
                            className={({ isActive }) =>
                                `text-sm hover:text-secondary ${isActive
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                }`
                            }
                        >
                            Pricing
                        </NavLink>

                    </div>

                    <div className="w-[320px] shrink-0 relative lg:block hidden">

                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search..."
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={handleInputClick}
                        />

                        {dropdownOpen && (
                            <div
                                ref={dropdownRef}
                                className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg z-50"
                            >
                                <ul className="divide-y divide-gray-200">
                                    {loading && results.length === 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            Loading...
                                        </li>
                                    )}

                                    {results.map((item, idx) => (
                                        <li
                                            key={item.id}
                                            ref={
                                                results.length === idx + 1
                                                    ? lastElementRef
                                                    : undefined
                                            }
                                            className="hover:bg-gray-100 cursor-pointer"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleItemClick(item.path);
                                            }}
                                        >
                                            <div className="block px-4 py-2">
                                                <span className="font-medium">
                                                    {item.keywords
                                                        .split(" ")
                                                        .map(
                                                            (word) =>
                                                                word.charAt(0).toUpperCase() +
                                                                word.slice(1)
                                                        )
                                                        .join(" ")}
                                                </span>
                                            </div>
                                        </li>
                                    ))}

                                    {loading && results.length > 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            Loading...
                                        </li>
                                    )}

                                    {!loading && results.length === 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            No results found.
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>

                    <button
                        className="lg:hidden block"
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                    >
                        <span className="material-symbols-outlined text-xl">
                            menu
                        </span>
                    </button>
                </div>
                <div className="lg:hidden block mt-3">
                    <div className="w-full relative">

                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search..."
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={handleInputClick}
                        />

                        {dropdownOpen && (
                            <div
                                ref={dropdownRef}
                                className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg z-50"
                            >
                                <ul className="divide-y divide-gray-200">
                                    {loading && results.length === 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            Loading...
                                        </li>
                                    )}

                                    {results.map((item, idx) => (
                                        <li
                                            key={item.id}
                                            ref={
                                                results.length === idx + 1
                                                    ? lastElementRef
                                                    : undefined
                                            }
                                            className="hover:bg-gray-100 cursor-pointer"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleItemClick(item.path);
                                            }}
                                        >
                                            <div className="block px-4 py-2">
                                                <span className="font-medium">
                                                    {item.keywords
                                                        .split(" ")
                                                        .map(
                                                            (word) =>
                                                                word.charAt(0).toUpperCase() +
                                                                word.slice(1)
                                                        )
                                                        .join(" ")}
                                                </span>
                                            </div>
                                        </li>
                                    ))}

                                    {loading && results.length > 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            Loading...
                                        </li>
                                    )}

                                    {!loading && results.length === 0 && (
                                        <li className="px-4 py-2 text-center text-gray-500">
                                            No results found.
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            <div className={`fixed top-0 left-0 w-full overflow-auto h-screen overflow-y-auto px-2 bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
                <div className="flex justify-end items-center p-2 border-b">
                    <button
                        onClick={() => setSidebarOpen(false)}
                    >
                        <span className="material-symbols-outlined text-2xl">
                            close
                        </span>
                    </button>
                </div>


                <div className="p-4 space-y-2">
                    <NavLink
                        to="/tutorial"
                        className={({ isActive }) =>
                            `text-sm hover:text-secondary ${isActive
                                ? "text-secondary font-medium"
                                : "text-blackRgba"
                            }`
                        }
                    >
                        Overview
                    </NavLink><br />

                    <NavLink
                        to="/docs/introduction"
                        className={({ isActive }) =>
                            `text-sm hover:text-secondary  ${isActive
                                ? "text-secondary font-medium"
                                : "text-blackRgba"
                            }`
                        }
                    >
                        Docs
                    </NavLink><br />
                    <NavLink
                        to="/features"
                        className={({ isActive }) =>
                            `text-sm hover:text-secondary  ${isActive
                                ? "text-secondary font-medium"
                                : "text-blackRgba"
                            }`
                        }
                    >
                        Features
                    </NavLink>
                    {navItems.map((item, index) => (
                        <div key={index}>
                            <button
                                className={`flex items-center text-sm ${item.children.some((c: any) => c.link === selected)
                                    ? "text-secondary font-medium"
                                    : "text-blackRgba"
                                    }`}
                                onClick={() =>
                                    setOpenDropdown(
                                        openDropdown === index ? null : index
                                    )
                                }
                            >
                                {item.name}
                                <span className="material-symbols-outlined">
                                    expand_more
                                </span>
                            </button>

                            {openDropdown === index && (
                                <div className="ml-4 mt-2 flex flex-col gap-2">
                                    {item.children.map((child: any, i: number) => (
                                        <NavLink
                                            key={i}
                                            to={child.link}
                                            className={`px-4 py-2 text-sm whitespace-nowrap ${selected === child.link
                                                ? "text-secondary font-medium"
                                                : "text-blackRgba"
                                                }`}
                                            onClick={() => {
                                                setSelected(child.link);
                                                setSidebarOpen(false);
                                            }}
                                        >
                                            {child.name}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                    )}

                    <NavLink
                        to="/faq"
                        className={({ isActive }) =>
                            `text-sm hover:text-secondary ${isActive
                                ? "text-secondary font-medium"
                                : "text-blackRgba"
                            }`
                        }

                    >
                        FAQ
                    </NavLink><br />

                    <NavLink
                        to="/pricing"
                        className={({ isActive }) =>
                            `text-sm hover:text-secondary ${isActive
                                ? "text-secondary font-medium"
                                : "text-blackRgba"
                            }`
                        }
                    >
                        Pricing
                    </NavLink>
                </div>


            </div>

            {
                isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                        onClick={() => setSidebarOpen(false)}
                    />
                )
            }
        </>

    );
};

export default HelpDocHeader;